<template>
  <div>
    <OfferTermsCardComponent
      ref="termsCard"
      :registration-authority="selectionAuthority"
      :has-selection
      :price-hint="$t('soldera_inventory.forward.price_hint')"
    />

    <OfferActionsComponent
      :title="$t('soldera_inventory.forward.select_mandates')"
      :can-submit
      :is-submitting
      :blocking-reason
      :show-blocking-reason="hasSelection"
      :sent="sentOffer"
      @submit="sendOffer"
    />

    <div class="mb-3">
      <PickerProductionRangeComponent v-model="periodForm" />
    </div>

    <div class="d-flex flex-column gap-3">
      <SelectionSummaryComponent
        :available="{
          mwh: sumBy(visibleMandates, 'capacity_total'),
          count: visibleMandates.length,
        }"
        :selected="{ mwh: selectedMwh, count: takenMandates.length }"
      >
        <BreakdownSectionComponent
          v-for="section in breakdownSections"
          :key="section.key"
          :title="section.title"
          :entries="section.entries"
          :total="breakdownTotal"
          :data-testid="`breakdown-${section.key}`"
        />
      </SelectionSummaryComponent>

      <div
        v-for="warning in productWarnings"
        :key="warning.kind"
        class="alert alert-warning py-2 mb-0 fs-9"
        role="status"
        :data-testid="`product-warning-${warning.kind}`"
      >
        <FontAwesomeIcon icon="fa-solid fa-circle-info" class="me-2" />
        {{ warningText(warning) }}
      </div>

      <AutoSelectToolbarComponent
        v-model:enabled="autoSelectEnabled"
        v-model:target-mwh="targetMwh"
        v-model:spread="spreadOverMandates"
        :spread-label="$t('soldera_inventory.forward.spread_over_mandates')"
        :can-clear="hasSelection"
        data-testid="forward-filters"
        @clear="takes = {}"
      >
        <select
          v-model="technologyFilter"
          class="form-select form-select-sm w-auto"
          :aria-label="$t('soldera_inventory.forward.columns.technology')"
        >
          <option :value="null">
            {{ $t("soldera_inventory.forward.all_technologies") }}
          </option>
          <option
            v-for="technology in technologyOptions"
            :key="technology"
            :value="technology"
          >
            {{ technologyName(technology) }}
          </option>
        </select>
        <select
          v-model="countryFilter"
          class="form-select form-select-sm w-auto"
          :aria-label="$t('soldera_inventory.forward.columns.country')"
        >
          <option :value="null">
            {{ $t("soldera_inventory.forward.all_countries") }}
          </option>
          <option
            v-for="country in countryOptions"
            :key="country"
            :value="country"
          >
            {{ country }}
          </option>
        </select>
        <button
          type="button"
          class="btn btn-sm"
          :class="standardAibGc ? 'btn-primary' : 'btn-outline-secondary'"
          data-testid="standard-aib-gc-filter"
          @click="standardAibGc = !standardAibGc"
        >
          {{ $t("inventory_selection.standard_aib_gc") }}
        </button>
      </AutoSelectToolbarComponent>

      <LoadingIndicatorComponent
        v-if="isLoadingMandates && mandates.length === 0"
      />
      <ForwardMandatesTableComponent
        v-else
        v-model:takes="takes"
        :mandates="visibleMandates"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { sumBy, uniq } from "lodash-es";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { STANDARD_AIB_GC_COUNTRIES } from "@/constants";
import {
  distributeTakes,
  type TakeMap,
} from "@/forwards2/picker/distributeTakes";
import PickerProductionRangeComponent from "@/forwards2/picker/PickerProductionRangeComponent.vue";
import { defaultPeriodForm, periodRange } from "@/forwards2/picker/pickerForm";
import { volumeByVintage } from "@/forwards2/picker/productionBreakdown";
import AutoSelectToolbarComponent from "@/inventory_selection/AutoSelectToolbarComponent.vue";
import BreakdownSectionComponent, {
  type BreakdownEntry,
} from "@/inventory_selection/BreakdownSectionComponent.vue";
import { groupMwh } from "@/inventory_selection/groupMwh";
import SelectionSummaryComponent from "@/inventory_selection/SelectionSummaryComponent.vue";
import { useAutoSelect } from "@/inventory_selection/useAutoSelect";
import { technologyName } from "@/types/technology";

import { offerBlockingKey, singleAuthority } from "../common/offerGate";
import OfferActionsComponent from "../common/OfferActionsComponent.vue";
import OfferTermsCardComponent from "../common/OfferTermsCardComponent.vue";
import { useLatestListing } from "../common/useLatestListing";
import { useOfferTerms } from "../common/useOfferTerms";
import { solderaInventoryApi } from "../solderaInventoryApi";
import type {
  ForwardInventoryMandate,
  ForwardInventoryResponse,
  OfferResponse,
} from "../solderaInventoryTypes";

import ForwardMandatesTableComponent from "./ForwardMandatesTableComponent.vue";
import { type SelectionWarning, selectionWarnings } from "./selectionWarnings";

/**
 * Forward tab of Soldera Inventory: future production from Soldera's forward mandates over the periods
 * the buyer picks, one take per mandate, bid on with the shared offer terms. An offer becomes a
 * draft forward deal that Soldera staff confirm.
 */

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const terms = useOfferTerms();
const termsCard =
  useTemplateRef<InstanceType<typeof OfferTermsCardComponent>>("termsCard");

const periodForm = ref(defaultPeriodForm());
const mandates = ref<ForwardInventoryMandate[]>([]);
// The periods the listed mandates were computed for; an offer is made against these, not the form.
const listedPeriods = ref<string[]>([]);
const takes = ref<TakeMap>({});
const technologyFilter = ref<string | null>(null);
const countryFilter = ref<string | null>(null);
const standardAibGc = ref(false);
const spreadOverMandates = ref(true);
const isSubmitting = ref(false);
const sentOffer = ref<OfferResponse | null>(null);

const pickedPeriods = computed(() => periodRange(periodForm.value));

// The mandates follow the range and the price (a mandate is listed only once the price clears its floor);
// until the refetch lands the listed mandates are the old ones, so sending waits for it.
const {
  isLoading: isLoadingMandates,
  listingFailed,
  refetch: fetchMandates,
} = useLatestListing(
  () => {
    const periods = pickedPeriods.value;
    if (periods.length === 0) return null;
    return solderaInventoryApi.forward
      .listMandates(periods, terms.listingPrice)
      .then(({ data }): ForwardInventoryResponse => data);
  },
  (data) => {
    mandates.value = data?.mandates ?? [];
    listedPeriods.value = data?.periods ?? [];
    takes.value = keepAvailableTakes(takes.value, mandates.value);
  },
  {
    sources: [pickedPeriods, () => terms.listingPrice],
    debounceMs: 400,
    onError: globalErrorHandler,
  }
);

const technologyOptions = computed(() =>
  uniq(mandates.value.flatMap((mandate) => mandate.technologies)).sort()
);
const countryOptions = computed(() =>
  uniq(mandates.value.flatMap((mandate) => mandate.countries)).sort()
);

const visibleMandates = computed(() =>
  mandates.value.filter(
    (mandate) =>
      (technologyFilter.value === null ||
        mandate.technologies.includes(technologyFilter.value)) &&
      (countryFilter.value === null ||
        mandate.countries.includes(countryFilter.value)) &&
      (!standardAibGc.value || isStandardAibGc(mandate))
  )
);

// Standard AIB GC: AIB certificates from the countries buyers accept as one standard product.
function isStandardAibGc(mandate: ForwardInventoryMandate): boolean {
  return (
    mandate.registration_authority === "AIB" &&
    mandate.countries.every((country) =>
      STANDARD_AIB_GC_COUNTRIES.includes(country)
    )
  );
}

const takenMandates = computed(() =>
  mandates.value.filter((mandate) => (takes.value[mandate.id] ?? 0) > 0)
);
const hasSelection = computed(() => takenMandates.value.length > 0);
const productWarnings = computed(() => selectionWarnings(takenMandates.value));
const selectedMwh = computed(() =>
  sumBy(takenMandates.value, (mandate) => takes.value[mandate.id] ?? 0)
);

// Auto-select works as on the Spot tab, filling the target from the mandates on screen.
const { enabled: autoSelectEnabled, targetMwh } = useAutoSelect({
  run: autoDistribute,
  sources: [spreadOverMandates, visibleMandates],
});

const selectedAuthorities = computed(
  () =>
    new Set(
      takenMandates.value.map((mandate) => mandate.registration_authority)
    )
);

// The scheme the counterparty's registry account must belong to; the picker validates against it.
const selectionAuthority = computed(() =>
  singleAuthority(selectedAuthorities.value)
);

// What the breakdowns describe: the takes once there are any, else what the filters show.
const breakdownMandates = computed(() =>
  hasSelection.value ? takenMandates.value : visibleMandates.value
);
const mandateMwh = (mandate: ForwardInventoryMandate): number =>
  hasSelection.value ? (takes.value[mandate.id] ?? 0) : mandate.capacity_total;
const breakdownTotal = computed(() =>
  sumBy(breakdownMandates.value, mandateMwh)
);

const breakdownSections = computed(() => [
  {
    key: "vintage",
    title: t("soldera_inventory.forward.by_period"),
    entries: volumeByVintage(
      breakdownMandates.value.map((mandate) => ({
        take: mandateMwh(mandate),
        capacity_by_period: mandate.capacity_by_period,
      }))
    ),
  },
  {
    key: "technology",
    title: t("inventory_selection.breakdowns.technology"),
    entries: breakdownEntries((mandate) =>
      mandate.technologies.map(technologyName).join(", ")
    ),
  },
  {
    key: "countries",
    title: t("soldera_inventory.forward.columns.country"),
    entries: breakdownEntries((mandate) => mandate.countries.join(", ")),
  },
  {
    key: "support",
    title: t("inventory_selection.breakdowns.support_status"),
    entries: breakdownEntries((mandate) =>
      t(`forwards.common.support.${mandate.support}`)
    ),
  },
]);

const blockingReason = computed((): string | null => {
  const key = offerBlockingKey({
    hasSelection: hasSelection.value,
    noSelectionKey: "soldera_inventory.forward.blocked.no_takes",
    termsBlockingKey: terms.blockingKey,
    authorities: selectedAuthorities.value,
  });
  return key && t(key);
});

const canSubmit = computed(
  () =>
    blockingReason.value === null &&
    !isSubmitting.value &&
    !isLoadingMandates.value &&
    !listingFailed.value
);

function breakdownEntries(
  keyOf: (mandate: ForwardInventoryMandate) => string
): BreakdownEntry[] {
  return [
    ...groupMwh(
      breakdownMandates.value,
      (mandate) => keyOf(mandate) || "–",
      mandateMwh
    ),
  ]
    .sort(([, a], [, b]) => b - a)
    .map(([label, mwh]) => ({ label, mwh }));
}

function warningText(warning: SelectionWarning): string {
  const key = `soldera_inventory.forward.warnings.${warning.kind}`;
  const list = (values: string[]) => values.join(", ");
  const technologies = (values: string[]) =>
    list(values.map((value) => technologyName(value)));
  if (warning.kind === "pl_be_mixed") {
    return t(key, {
      flagged: list(warning.flagged),
      others: list(warning.others),
    });
  }
  if (warning.kind === "standard_aib_gc_mixed") {
    return t(key, {
      standard: list(warning.standard),
      outside: list(warning.outside),
    });
  }
  return t(key, {
    advanced: technologies(warning.advanced),
    hsw: technologies(warning.hsw),
  });
}

// A take survives a refetch only on a mandate still listed, and never above what that mandate can now sell.
function keepAvailableTakes(
  current: TakeMap,
  listed: ForwardInventoryMandate[]
): TakeMap {
  const next: TakeMap = {};
  for (const mandate of listed) {
    const take = Math.min(current[mandate.id] ?? 0, mandate.capacity_total);
    if (take > 0) next[mandate.id] = take;
  }
  return next;
}

function autoDistribute(targetMwh: number) {
  const rows = visibleMandates.value.map((mandate) => ({
    mandate_id: mandate.id,
    capacity_total: mandate.capacity_total,
    // A buyer never sees a mandate's floor, so the greedy fill orders by size alone.
    minimum_price: null,
  }));
  takes.value = distributeTakes(
    rows,
    targetMwh,
    spreadOverMandates.value,
    takes.value
  );
}

async function sendOffer() {
  if (!canSubmit.value || !termsCard.value?.validate()) return;
  isSubmitting.value = true;
  try {
    const { data } = await solderaInventoryApi.forward.createOffer({
      periods: listedPeriods.value,
      takes: takes.value,
      ...terms.toRequest(),
    });
    globalSuccessHandler(
      t("soldera_inventory.created_title"),
      t("soldera_inventory.created_description", { mwh: data.volume_mwh })
    );
    sentOffer.value = data;
    takes.value = {};
    await fetchMandates();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>
